import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, SlidersHorizontal, Type, Minus, Plus, BookmarkCheck, Route, X, Share2, Headphones, Flame, StickyNote } from 'lucide-react';
import { useAuthGate } from '../hooks/useAuthGate';
import { usePremiumGate } from '../hooks/usePremiumGate';
import { Switch } from './ui/switch';
import { mockTranslations } from '../mockData';
import { getBookName } from '../utils/bookTranslations';
import { toast } from 'sonner';
import VerseActionMenu from './VerseActionMenu';
import VerseActionMenuMobile from './VerseActionMenuMobile';
import SocialShareDialog from './SocialShareDialog';
import CreatePostModal from './CreatePostModal';
import QuickCompareDialog from './QuickCompareDialog';
import EnhancedVerseImageCustomizer from './EnhancedVerseImageCustomizer';
import AudioBiblePlayer from './AudioBiblePlayer';
import AmbientSceneLauncher from './AmbientSceneLauncher';
import StrongsVerseText from './StrongsVerseText';
import StrongsModal from './StrongsModal';
import useLongPressWord from '../hooks/useLongPressWord';
import InterlinearVerse from './InterlinearVerse';
import NameMeaningText from './NameMeaningText';
import ChapterNamesPanel from './ChapterNamesPanel';
import InterlinearOverlayHint from './InterlinearOverlayHint';
import NowFreeBadge, { markNowFreeSeen } from './NowFreeBadge';
import MatthewHenryButton from './MatthewHenryButton';
import { FuzzySearchInput, SmartSynopsisCard } from './AiBibleTools';
import JfbCommentaryButton from './JfbCommentaryButton';
import ClarkeCommentaryButton from './ClarkeCommentaryButton';
import KeilCommentaryButton from './KeilCommentaryButton';
import TyndaleCommentaryButton from './TyndaleCommentaryButton';
import CompareCommentariesButton from './CompareCommentariesButton';
import useInterlinearOverlay from '../hooks/useInterlinearOverlay';
import { BibleIntroButton } from './BibleIntroDialog';
import OldSpanishText, { OLD_SPELLING_TRANSLATIONS } from './OldSpanishText';
import { strongsTranslationFor, ALL_BOOKS, booksFor, isBookAvailable } from '../constants/bibleConstants';
import { parseReferenceString } from '../utils/referenceParser';
import VerseHighlightDialog from './VerseHighlightDialog';
import BibleReaderHeader from './BibleReaderHeader';
import OfflineBibleSettings from './OfflineBibleSettings';
import SaveBookOfflineButton from './SaveBookOfflineButton';
import VerseTrailChip from './VerseTrailChip';
import { recordGuestVerseAction } from './GuestUpgradeNudge';
import { useChapterLoader, useVerseTrail, useChapterHighlights } from '../hooks/useChapterReader';
import { resolveTranslationSelection } from '../utils/translationSelection';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { LS } from '../utils/lang';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bible Reader - Chapter View for Standalone Bible Page
 * Shows entire chapter with all verses, YouVersion features on click
 */
const BibleReaderChapterView = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  // Deep-link params: /bible/:book/:chapter (book may contain spaces, URL-encoded).
  const { book: bookParam, chapter: chapterParam } = useParams();
  // "Remember last read": with no deep-link params, reopen to the exact book,
  // chapter and version the user last read (stored in lastReadingPosition).
  const lastRead = (() => {
    try { return JSON.parse(localStorage.getItem('lastReadingPosition') || 'null'); } catch (_) { return null; }
  })();
  const hasLastRead = !bookParam && lastRead && ALL_BOOKS.includes(lastRead.book);
  const initialBook = bookParam && ALL_BOOKS.includes(decodeURIComponent(bookParam))
    ? decodeURIComponent(bookParam)
    : (hasLastRead ? lastRead.book : 'John');
  const initialChapter = chapterParam && !isNaN(parseInt(chapterParam, 10))
    ? Math.max(1, parseInt(chapterParam, 10))
    : (bookParam ? 1 : (hasLastRead && lastRead.chapter ? Math.max(1, parseInt(lastRead.chapter, 10)) : 3));
  const [selectedBook, setSelectedBook] = useState(initialBook);
  const [selectedChapter, setSelectedChapter] = useState(initialChapter);
  // Restore the user's last-selected translation so it survives a hard refresh.
  // RVR1960 is copyrighted (UBS) and can't be self-hosted, so map it to the
  // closest free public-domain match (Reina-Valera Gómez).
  const [selectedTranslation, setSelectedTranslation] = useState(() => {
    // Respect a saved choice; otherwise default by app language — Spanish → RVG
    // (Reina-Valera), English → KJV.
    const stored = localStorage.getItem('preferred_translation');
    if (!stored) return language === 'es' ? 'RVG' : 'KJV';
    return stored === 'RVR1960' ? 'RVG' : stored;
  });
  const [isMobile, setIsMobile] = useState(false);
  
  // YouVersion action menu state
  const [selectedVerseForAction, setSelectedVerseForAction] = useState(null);
  // Multi-verse selection: tap verses to accumulate, then act on all of them.
  const [selectedVerseNumbers, setSelectedVerseNumbers] = useState([]);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isSocialShareOpen, setIsSocialShareOpen] = useState(false);
  const [isPostToCommunityOpen, setIsPostToCommunityOpen] = useState(false);
  const [isVerseImageDialogOpen, setIsVerseImageDialogOpen] = useState(false);
  const [isQuickCompareOpen, setIsQuickCompareOpen] = useState(false);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  // How the audio player was opened: 'verse' (from a verse's Listen action) or
  // 'chapter' (from the chapter-level "Listen" button → hands-free full chapter).
  const [audioInitialScope, setAudioInitialScope] = useState('verse');
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isHighlightDialogOpen, setIsHighlightDialogOpen] = useState(false);
  const [isOfflineDialogOpen, setIsOfflineDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  
  // Font customization
  const [fontSize, setFontSize] = useState('base'); // 'sm', 'base', 'lg', 'xl', '2xl'
  const [fontFamily, setFontFamily] = useState('serif'); // 'serif', 'sans', 'mono'
  
  // "Continue where you left off" — capture the PREVIOUS session's position at
  // first render. A useState initializer runs during render (before effects),
  // so it survives the save-effect below which overwrites localStorage with the
  // current view. (The old code read it in a mount effect that the save-effect
  // clobbered on the same tick, so the card never appeared.)
  const [resumePosition] = useState(() => {
    try {
      const raw = localStorage.getItem('lastReadingPosition');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  const [resumeDismissed, setResumeDismissed] = useState(false);

  // Daily reading streak (logged-in users). Recorded when a chapter loads and
  // surfaced on the "Continue where you left off" card to reward return visits.
  const [readingStats, setReadingStats] = useState(null);
  const recordedChaptersRef = React.useRef(new Set());

  // Interlinear toggle — when ON, tagged verses render with original Hebrew/Greek + translit
  // beneath each English word. Overlay + lexicon are managed by useInterlinearOverlay
  // which keeps a per-chapter Map cache so cross-chapter toggling is instant.
  const [interlinearOn, setInterlinearOn] = useState(false);
  // Name-meaning chips: tappable Hitchcock name meanings on plain verses.
  const [namesOn, setNamesOn] = useState(false);
  // Collapsible study-tools tray — keeps the reading view clean by default.
  const [toolsExpanded, setToolsExpanded] = useState(() => {
    try { return localStorage.getItem('reader_tools_expanded') === '1'; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('reader_tools_expanded', toolsExpanded ? '1' : '0'); } catch (_) { /* noop */ }
  }, [toolsExpanded]);
  // Guest gating: Bible reading is free; interactions require a (free) account.
  // Premium gating: Strong's, dictionaries/name-meanings, interlinear, and
  // commentaries require a paid Lumera+ plan (guest → login, free → upgrade).
  const { isGuest, requireAuth, dialog: authDialog } = useAuthGate();
  const { dialog: premiumDialog } = usePremiumGate();

  // Chapter data + verse-trail + highlights are managed by dedicated hooks.
  const { chapterVerses, chapterSource, isLoading, isUnavailable, fallbackTranslation, totalChapters } =
    useChapterLoader(selectedBook, selectedChapter, selectedTranslation);
  const trailCounts = useVerseTrail(selectedBook, selectedChapter, isGuest);
  // Catholic-only books (e.g. Tobit) vanish when switching to a 66-book Bible.
  useEffect(() => {
    if (!isBookAvailable(selectedTranslation, selectedBook)) { setSelectedBook('Genesis'); setSelectedChapter(1); }
  }, [selectedTranslation, selectedBook]);
  const [highlights, setHighlights] = useChapterHighlights(selectedBook, selectedChapter, selectedTranslation, isGuest);
  const [notesMap, setNotesMap] = useState({}); // verse_reference -> note_text (inline notes while reading)

  // Note Sync: load this chapter's saved notes so they surface inline on the verse.
  useEffect(() => {
    let cancelled = false;
    const loadChapterNotes = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/user/notes`, { withCredentials: true });
        const all = r.data.notes || r.data || [];
        const prefix = `${selectedBook} ${selectedChapter}:`;
        const map = {};
        all.forEach((n) => {
          const ref = n.verse_reference || n.reference;
          if (ref && ref.startsWith(prefix)) map[ref] = n.note_text || n.note || n.content || '';
        });
        if (!cancelled) setNotesMap(map);
      } catch (e) { if (!cancelled) setNotesMap({}); }
    };
    loadChapterNotes();
    return () => { cancelled = true; };
  }, [selectedBook, selectedChapter, isNoteDialogOpen]);

  const handleSelectTranslation = (code) => {
    // Pure resolver keeps the copyright-redirect logic unit-tested
    // (see utils/translationSelection.js + its spec).
    const { translation, redirected } = resolveTranslationSelection(code);
    if (redirected) toast.info(t('reader.rvr1960Copyright'));
    setSelectedTranslation(translation);
    localStorage.setItem('preferred_translation', translation);
  };

  // Follow the app language: switching to Spanish shows Reina-Valera (RVG) and
  // switching to English shows KJV — but only when the current version belongs
  // to the other language, so an explicit in-language choice (e.g. ESS) is kept.
  const prevLangRef = React.useRef(language);
  useEffect(() => {
    if (prevLangRef.current === language) return;
    prevLangRef.current = language;
    const esAbbrs = ['RVG', 'RVR1909', 'RVR1960', 'ONBV', 'ESS', 'PDPT', 'BLM', 'VBL', 'RV1865', 'TA', 'BLL', 'JUN', 'RV1602', 'RV1569'];
    const curIsEs = esAbbrs.includes(selectedTranslation);
    if (language === 'es' && !curIsEs) handleSelectTranslation('RVG');
    else if (language === 'en' && curIsEs) handleSelectTranslation('KJV');
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleNamesToggle = (v) => {
    // Name meanings & dictionaries are now free.
    if (v) markNowFreeSeen('names');
    setNamesOn(v);
  };
  const handleInterlinearToggle = (v) => {
    // Strong's interlinear is now free.
    if (v) markNowFreeSeen('interlinear');
    setInterlinearOn(v);
  };
  // Study Trail — the chain of cross-reference jumps taken from name profiles,
  // so users can step back through their study path. Persisted per-session.
  const [studyTrail, setStudyTrail] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('study_trail') || '[]'); } catch { return []; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('study_trail', JSON.stringify(studyTrail)); } catch { /* quota */ }
  }, [studyTrail]);
  // Spanish reading translations overlay the RV1909+Strong's tagged text (and
  // Spanish concordance); everything else uses KJV+Strong's.
  const strongsTranslation = strongsTranslationFor(selectedTranslation);
  const {
    taggedByVerse,
    lexicon: interlinearLexicon,
    loading: loadingInterlinear,
  } = useInterlinearOverlay(interlinearOn, selectedBook, selectedChapter, strongsTranslation);
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Touch/swipe navigation for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Save reading position whenever book/chapter changes (for next session's
  // "Continue where you left off" card).
  useEffect(() => {
    const position = {
      book: selectedBook,
      chapter: selectedChapter,
      translation: selectedTranslation,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('lastReadingPosition', JSON.stringify(position));
    localStorage.setItem('preferred_translation', selectedTranslation);
  }, [selectedBook, selectedChapter, selectedTranslation]);

  // Load the reader's current reading streak on open (logged-in users only).
  useEffect(() => {
    if (isGuest) { setReadingStats(null); return; }
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/profile/stats`, { withCredentials: true })
      .then((r) => { if (!cancelled) setReadingStats(r.data || null); })
      .catch(() => { if (!cancelled) setReadingStats(null); });
    return () => { cancelled = true; };
  }, [isGuest]);

  // Record a reading session when a chapter loads (once per chapter per session)
  // so the daily streak advances from real reading. Updates the streak badge.
  useEffect(() => {
    if (isGuest) return;
    if (!chapterVerses || chapterVerses.length === 0) return;
    const key = `${selectedBook} ${selectedChapter}`;
    if (recordedChaptersRef.current.has(key)) return;
    recordedChaptersRef.current.add(key);
    axios.post(
      `${BACKEND_URL}/api/profile/activity`,
      { verses_read: chapterVerses.length, chapters_read: 1 },
      { withCredentials: true }
    )
      .then((r) => {
        if (r.data && typeof r.data.current_streak === 'number') {
          const streak = r.data.current_streak;
          setReadingStats((prev) => ({
            ...(prev || {}),
            current_streak: streak,
            longest_streak: r.data.longest_streak,
          }));
          // Celebrate 7/30/100-day milestones with a shareable card (once each).
          if ([7, 30, 100].includes(streak)) {
            const milestoneKey = `reading_milestone_${streak}`;
            if (!localStorage.getItem(milestoneKey)) {
              localStorage.setItem(milestoneKey, '1');
              window.dispatchEvent(new CustomEvent('lumera:milestone-share', {
                detail: {
                  message: t('reader.streakMilestone', { streak }),
                },
              }));
            }
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, selectedBook, selectedChapter, chapterVerses]);

  // Keep the URL in sync for shareable deep links: /bible/John/3
  useEffect(() => {
    const target = `/bible/${encodeURIComponent(selectedBook)}/${selectedChapter}`;
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [selectedBook, selectedChapter, navigate]);

  // Handle swipe gestures for chapter navigation
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && selectedChapter < totalChapters) {
      // Swipe left = next chapter
      goToNextChapter();
    }
    if (isRightSwipe && selectedChapter > 1) {
      // Swipe right = previous chapter
      goToPreviousChapter();
    }
  };


  const goToPreviousChapter = () => {
    if (selectedChapter > 1) {
      setSelectedChapter(selectedChapter - 1);
    } else {
      const books = booksFor(selectedTranslation);
      const currentIndex = books.indexOf(selectedBook);
      if (currentIndex > 0) {
        setSelectedBook(books[currentIndex - 1]);
        setSelectedChapter(150); // Will adjust when loaded
      }
    }
  };

  const goToNextChapter = () => {
    setSelectedChapter(selectedChapter + 1);
  };

  // Audio autoplay → next chapter. The player calls this when a chapter ends;
  // we advance the reader, then (once the new verses load) bump the token so
  // the player resumes from verse 1. Returns false at the end of the book.
  const [audioNextChapterToken, setAudioNextChapterToken] = useState(0);
  const audioNextReqRef = React.useRef(false);
  const requestAudioNextChapter = () => {
    if (selectedChapter < totalChapters) {
      audioNextReqRef.current = true;
      setSelectedChapter((c) => c + 1);
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (audioNextReqRef.current && !isLoading && chapterVerses && chapterVerses.length > 0) {
      audioNextReqRef.current = false;
      setAudioNextChapterToken((n) => n + 1);
    }
  }, [chapterVerses, isLoading]);

  // Cross-reference jump: navigate the reader to a scripture reference tapped
  // inside a name mini-profile. Validates the book, records the study trail,
  // then updates URL + state.
  const handleRefNavigate = (book, chapter) => {
    if (!book || !ALL_BOOKS.includes(book)) {
      toast.error(
        t('reader.couldNotOpen', { ref: book || t('reader.theReference') })
      );
      return;
    }
    const ch = Math.max(1, parseInt(chapter, 10) || 1);
    // Skip if we're already exactly here.
    if (book === selectedBook && ch === selectedChapter) return;
    setStudyTrail((prev) => {
      const here = { book: selectedBook, chapter: selectedChapter };
      const base = prev.length === 0 ? [here] : prev;
      return [...base, { book, chapter: ch }];
    });
    setSelectedBook(book);
    setSelectedChapter(ch);
    navigate(`/bible/${encodeURIComponent(book)}/${ch}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // The in-reader verse search (and other tools) fire `lumera:verse-select`.
  // Handle it here so a clicked result actually opens its chapter even when the
  // reader is already mounted (the dashboard listener only sets a prop that an
  // already-mounted reader ignores). A ref keeps the handler on the latest state.
  const verseSelectNavRef = React.useRef(() => {});
  verseSelectNavRef.current = (ref) => {
    const parsed = parseReferenceString(ref);
    if (!parsed || !ALL_BOOKS.includes(parsed.book)) return;
    handleRefNavigate(parsed.book, parsed.chapter);
  };
  useEffect(() => {
    const handler = (e) => verseSelectNavRef.current(e?.detail?.reference);
    window.addEventListener('lumera:verse-select', handler);
    return () => window.removeEventListener('lumera:verse-select', handler);
  }, []);

  // Changing the book resets to chapter 1 so we never land on a chapter that
  // doesn't exist in a shorter book (e.g. John 21 -> Jude would 404 at Jude 21).
  const handleSelectBook = (book) => {
    if (!book || book === selectedBook) { setSelectedBook(book); return; }
    setSelectedBook(book);
    setSelectedChapter(1);
    navigate(`/bible/${encodeURIComponent(book)}/1`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // Jump back to a point in the study trail, truncating anything after it.
  const jumpToTrail = (index) => {
    const step = studyTrail[index];
    if (!step) return;
    setStudyTrail((prev) => prev.slice(0, index + 1));
    setSelectedBook(step.book);
    setSelectedChapter(step.chapter);
    navigate(`/bible/${encodeURIComponent(step.book)}/${step.chapter}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Save the current study trail and copy/share a public link to it.
  const [savingPath, setSavingPath] = useState(false);
  const saveAndShareTrail = async () => {
    if (!requireAuth(t('reader.savingStudyPaths'))) return;
    if (studyTrail.length < 2) return;
    setSavingPath(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/study-paths`,
        { steps: studyTrail.map((s) => ({ book: s.book, chapter: s.chapter })) },
        { withCredentials: true },
      );
      // Share the OG-unfurl URL so social apps show a rich preview; it 302s
      // real browsers into the /path/:id viewer.
      const url = `${BACKEND_URL}/api/study-paths/${res.data.id}/share`;
      if (navigator.share) {
        try {
          await navigator.share({ title: 'Lumera · Study Path', text: 'Follow my Bible study path', url });
        } catch (_) { /* user cancelled */ }
      } else {
        await navigator.clipboard.writeText(url);
      }
      toast.success(t('reader.studyPathSaved'));
    } catch (e) {
      toast.error(t('reader.studyPathSaveError'));
    } finally {
      setSavingPath(false);
    }
  };

  // Verse click handler - toggles verse into the multi-select set
  const handleVerseClick = (verse) => {
    if (consumeLongPress()) return;
    const already = selectedVerseNumbers.includes(verse.verse);
    const next = already
      ? selectedVerseNumbers.filter((n) => n !== verse.verse)
      : [...selectedVerseNumbers, verse.verse];
    setSelectedVerseNumbers(next);
    if (next.length === 0) {
      setIsActionMenuOpen(false);
      setSelectedVerseForAction(null);
      return;
    }
    setSelectedVerseForAction({
      ...verse,
      reference: `${selectedBook} ${selectedChapter}:${verse.verse}`,
      translation: selectedTranslation,
      book: selectedBook,
      chapter: selectedChapter,
      verseNumber: verse.verse,
    });
    setIsActionMenuOpen(true);
  };

  const handleCloseActionMenu = () => {
    setIsActionMenuOpen(false);
    setSelectedVerseForAction(null);
    setSelectedVerseNumbers([]);
  };

  // Verse objects currently selected (sorted), falling back to the primary verse.
  const getSelectedVerseObjs = () => {
    const objs = chapterVerses.filter((v) => selectedVerseNumbers.includes(v.verse));
    if (objs.length) return [...objs].sort((a, b) => a.verse - b.verse);
    if (selectedVerseForAction) {
      return [{ verse: selectedVerseForAction.verseNumber || selectedVerseForAction.verse, text: selectedVerseForAction.text }];
    }
    return [];
  };

  // Safety net: Radix can leave body{pointer-events:none} after a dialog that
  // was conditionally unmounted while open, which blocks the next click. Reset
  // it once every verse-action overlay is closed.
  useEffect(() => {
    const anyOverlayOpen = isActionMenuOpen || isNoteDialogOpen || isSocialShareOpen ||
      isPostToCommunityOpen || isQuickCompareOpen || isVerseImageDialogOpen ||
      isAudioPlayerOpen || isHighlightDialogOpen;
    if (!anyOverlayOpen) {
      const timer = setTimeout(() => {
        if (document.body.style.pointerEvents === 'none') document.body.style.pointerEvents = '';
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [isActionMenuOpen, isNoteDialogOpen, isSocialShareOpen, isPostToCommunityOpen,
      isQuickCompareOpen, isVerseImageDialogOpen, isAudioPlayerOpen, isHighlightDialogOpen]);

  // Action handlers
  const handleSaveVerse = async () => {
    if (!requireAuth(t('reader.savingVerses'))) { handleCloseActionMenu(); return; }
    if (!selectedVerseForAction) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/bookmarks`,
        {
          verse_reference: selectedVerseForAction.reference,
          verse_text: selectedVerseForAction.text,
          translation: selectedVerseForAction.translation || selectedTranslation
        },
        { withCredentials: true }
      );
      toast.success(t('reader.verseSaved'));
      handleCloseActionMenu();
    } catch (error) {
      console.error('Error saving verse:', error);
      toast.error(t('reader.verseSaveError'));
    }
  };

  const handleNoteVerse = () => {
    if (!requireAuth(t('reader.notes'))) { handleCloseActionMenu(); return; }
    setNoteText('');
    setIsNoteDialogOpen(true);
    setIsActionMenuOpen(false);  // keep selectedVerseForAction for the dialog
  };

  const handleSaveNote = async () => {
    if (!selectedVerseForAction || !noteText.trim()) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/user/notes`,
        {
          verse_reference: selectedVerseForAction.reference,
          note_text: noteText,
        },
        { withCredentials: true }
      );
      toast.success(t('reader.noteSaved'));
      setIsNoteDialogOpen(false);
      setNoteText('');
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(t('reader.noteSaveError'));
    }
  };

  const handleCopyVerse = async () => {
    const objs = getSelectedVerseObjs();
    if (!objs.length) return;
    if (isGuest) recordGuestVerseAction();  // strong intent signal → guest nudge

    let textToCopy;
    if (objs.length === 1) {
      textToCopy = `"${objs[0].text}"\n\n${selectedBook} ${selectedChapter}:${objs[0].verse} (${selectedTranslation})`;
    } else {
      const body = objs.map((v) => `${v.verse}. ${v.text}`).join(' ');
      textToCopy = `"${body}"\n\n${selectedBook} ${selectedChapter}:${objs[0].verse}-${objs[objs.length - 1].verse} (${selectedTranslation})`;
    }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for insecure contexts / older browsers
        const ta = document.createElement('textarea');
        ta.value = textToCopy;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(t('reader.copiedClipboard'));
    } catch (e) {
      toast.error(t('reader.couldNotCopy'));
    } finally {
      handleCloseActionMenu();  // always reset state so the menu can reopen
    }
  };

  const handleShareVerse = () => {
    const objs = getSelectedVerseObjs();
    if (objs.length > 1) {
      // Combine selected verses into a single shareable block.
      const body = objs.map((v) => `${v.verse}. ${v.text}`).join(' ');
      setSelectedVerseForAction((p) => ({
        ...(p || {}),
        text: body,
        reference: `${selectedBook} ${selectedChapter}:${objs[0].verse}-${objs[objs.length - 1].verse}`,
        translation: selectedTranslation,
      }));
    }
    setIsSocialShareOpen(true);
    setIsActionMenuOpen(false);  // Close action menu but KEEP selectedVerseForAction
  };

  const handlePostToCommunity = () => {
    if (!requireAuth(t('reader.postingCommunity'))) { handleCloseActionMenu(); return; }
    setIsPostToCommunityOpen(true);
    setIsActionMenuOpen(false);  // Close action menu but KEEP selectedVerseForAction
  };

  const handleImageVerse = () => {
    setIsVerseImageDialogOpen(true);
    setIsActionMenuOpen(false);  // Close action menu but KEEP selectedVerseForAction
  };

  const handleCompareVerse = () => {
    setIsQuickCompareOpen(true);
    setIsActionMenuOpen(false);  // Close action menu but KEEP selectedVerseForAction
  };

  const handleHighlightVerse = () => {
    if (isGuest) recordGuestVerseAction();  // strong intent signal → guest nudge
    if (!requireAuth(t('reader.highlights'))) { handleCloseActionMenu(); return; }
    setIsHighlightDialogOpen(true);
    setIsActionMenuOpen(false);  // Close action menu but KEEP selectedVerseForAction
  };

  const handleAudioVerse = () => {
    // Add translation to verse for TTS language detection BEFORE opening dialog
    if (selectedVerseForAction) {
      const verseWithTranslation = {
        ...selectedVerseForAction,
        translation: selectedTranslation
      };
      setSelectedVerseForAction(verseWithTranslation);
    }
    setAudioInitialScope('verse');
    setIsAudioPlayerOpen(true);
    setIsActionMenuOpen(false);  // Close action menu but KEEP selectedVerseForAction
  };

  // Hands-free "Listen" for the whole current chapter. Anchors the player to
  // the first verse (so it has book/chapter context + human narration) and
  // opens it in full-chapter scope.
  const handleListenChapter = () => {
    if (chapterVerses && chapterVerses.length > 0) {
      setSelectedVerseForAction({ ...chapterVerses[0], translation: selectedTranslation });
    }
    setAudioInitialScope('chapter');
    setIsAudioPlayerOpen(true);
  };

  const handleApplyHighlight = async (color) => {
    if (!selectedVerseForAction) return;
    return persistHighlight(color, undefined);
  };

  const handleApplyHighlightWithNote = async (color, note) => {
    if (!selectedVerseForAction) return;
    return persistHighlight(color, note);
  };

  // Single shared persistence path so both color-only and color+note share
  // the same backend shape and local-state update logic. The CreateHighlightRequest
  // model expects { reference, book, chapter, verse, text, color, note? }.
  const persistHighlight = async (color, note) => {
    const objs = getSelectedVerseObjs();
    if (!objs.length) return;
    const refOf = (v) => `${selectedBook} ${selectedChapter}:${v.verse}`;

    try {
      if (color === null) {
        // Remove highlight from every selected verse
        for (const v of objs) {
          await axios.delete(
            `${BACKEND_URL}/api/highlights/${encodeURIComponent(refOf(v))}`,
            { withCredentials: true }
          );
        }
        setHighlights(prev => {
          const updated = { ...prev };
          objs.forEach((v) => { delete updated[refOf(v)]; });
          return updated;
        });
        toast.success(t('reader.highlightRemoved'));
        return;
      }

      // Add/update highlight on every selected verse (backend CreateHighlightRequest shape).
      for (const v of objs) {
        const body = {
          reference: refOf(v),
          book: selectedBook,
          chapter: selectedChapter,
          verse: v.verse,
          text: v.text,
          color,
        };
        if (note !== undefined && note !== null) body.note = note;
        await axios.post(`${BACKEND_URL}/api/highlights/`, body, { withCredentials: true });
      }

      setHighlights(prev => {
        const updated = { ...prev };
        objs.forEach((v) => { updated[refOf(v)] = color; });
        return updated;
      });
      toast.success(
        note
          ? t('reader.highlightedSavedNote')
          : t('reader.verseHighlighted')
      );

      // Create activity for Community Feed (best-effort, primary verse only)
      try {
        const primary = objs[0];
        await axios.post(
          `${BACKEND_URL}/api/community/activity`,
          {
            type: 'highlight',
            data: {
              verse_reference: refOf(primary),
              verse_text: (primary.text || '').substring(0, 100) + ((primary.text || '').length > 100 ? '...' : ''),
              color: color,
              translation: selectedTranslation
            }
          },
          { withCredentials: true }
        );
      } catch (e) {
        /* community feed is best-effort */
      }
    } catch (e) {
      console.error('Highlight error:', e);
      toast.error(t('reader.highlightError'));
    }
  };

  // Long-press a word → open its Strong's word study (resolved via the tagged twin translation).
  const [wordStudyId, setWordStudyId] = useState(null);
  const { bind: bindLongPress, consumeClick: consumeLongPress } = useLongPressWord(async (word, reference) => {
    const lang = strongsTranslationFor(selectedTranslation).startsWith('RV1909') ? 'es' : 'en';
    try {
      const r = await axios.get(`${BACKEND_URL}/api/strongs/word-at`, { params: { reference, word, lang } });
      setWordStudyId(r.data.strongs_id);
    } catch {
      toast(language === 'es' ? `No se encontró la palabra original de "${word}"` : `No original word found for "${word}"`);
    }
  });

  return (
    <div className="bg-card rounded-lg shadow-lg p-4 sm:p-6 md:p-8 overflow-x-hidden max-w-full">
      {/* Continue where you left off — appears on open when the last-read
          position (from a previous session) differs from the current view. */}
      {resumePosition && !resumeDismissed &&
       (resumePosition.book !== selectedBook ||
        resumePosition.chapter !== selectedChapter ||
        resumePosition.translation !== selectedTranslation) && (
        <div
          className="mb-4 sm:mb-6 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          data-testid="continue-reading-card"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <BookmarkCheck className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
            <div>
              <p className="font-medium text-xs sm:text-sm">
                {t('reader.continueWhereLeft')}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground" data-testid="continue-reading-ref">
                {getBookName(resumePosition.book, resumePosition.translation, language)} {resumePosition.chapter} ({resumePosition.translation})
              </p>
              {readingStats && readingStats.current_streak > 0 && (
                <p
                  className="mt-1 inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-orange-500"
                  data-testid="reading-streak-badge"
                >
                  <Flame className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  {readingStats.current_streak}{' '}
                  {readingStats.current_streak === 1 ? t('reader.readingStreakUnitOne') : t('reader.readingStreakUnit')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => {
                setSelectedBook(resumePosition.book);
                setSelectedChapter(resumePosition.chapter);
                setSelectedTranslation(resumePosition.translation);
                setResumeDismissed(true);
              }}
              data-testid="continue-reading-go-btn"
            >
              {t('reader.resume')}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setResumeDismissed(true)}
              aria-label={t('reader.dismiss')}
              data-testid="continue-reading-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Navigation Controls */}
      <BibleReaderHeader
        selectedTranslation={selectedTranslation}
        setSelectedTranslation={handleSelectTranslation}
        selectedBook={selectedBook}
        setSelectedBook={handleSelectBook}
        selectedChapter={selectedChapter}
        setSelectedChapter={setSelectedChapter}
        totalChapters={totalChapters}
        fontSize={fontSize}
        setFontSize={setFontSize}
        fontFamily={fontFamily}
        setFontFamily={setFontFamily}
        onOpenOffline={() => setIsOfflineDialogOpen(true)}
        language={language}
      />

      {/* Chapter Title */}
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-center mb-2">
          {getBookName(selectedBook, selectedTranslation, language)} {selectedChapter}
        </h2>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <p className="text-center text-muted-foreground">{selectedTranslation}</p>
          {chapterSource === 'offline' && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
              title={t('reader.offlineCacheTitle')}
              data-testid="bible-offline-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              {t('reader.offline')}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={handleListenChapter}
            disabled={isLoading || !chapterVerses || chapterVerses.length === 0}
            data-testid="listen-chapter-btn"
            title={t('reader.listenTitle')}
          >
            <Headphones className="w-3.5 h-3.5 mr-1" />
            {t('reader.listen')}
          </Button>
          <Button
            variant={toolsExpanded ? 'default' : 'outline'}
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={() => setToolsExpanded((v) => !v)}
            data-testid="reader-tools-toggle"
            aria-expanded={toolsExpanded}
          >
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />
            {language === 'es' ? 'Herramientas' : 'Tools'}
            {toolsExpanded ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
          </Button>
        </div>

        {/* Collapsible study-tools tray — tucked away by default for a clean,
            uncluttered reading view (commentaries, scene, AI search, synopses,
            interlinear + name-meaning toggles). */}
        {toolsExpanded && (
          <div className="mt-3 space-y-3" data-testid="reader-tools-panel">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <SaveBookOfflineButton
                book={selectedBook}
                translation={selectedTranslation}
                language={language}
              />
              <AmbientSceneLauncher language={language} />
              <MatthewHenryButton
                book={selectedBook}
                chapter={selectedChapter}
                language={language}
              />
              <JfbCommentaryButton
                book={selectedBook}
                chapter={selectedChapter}
                language={language}
              />
              <ClarkeCommentaryButton
                book={selectedBook}
                chapter={selectedChapter}
                language={language}
              />
              <KeilCommentaryButton
                book={selectedBook}
                chapter={selectedChapter}
                language={language}
              />
              <TyndaleCommentaryButton
                book={selectedBook}
                chapter={selectedChapter}
                language={language}
              />
              <CompareCommentariesButton
                book={selectedBook}
                chapter={selectedChapter}
                language={language}
              />
            </div>

            {/* AI-powered tools strip: fuzzy/semantic verse search + smart
                chapter & book synopses. */}
            <div
              className="flex flex-wrap items-start gap-2 p-3 rounded-lg border-2 border-dashed border-purple-200 dark:border-purple-800 bg-gradient-to-r from-purple-50/50 to-teal-50/50 dark:from-purple-950/20 dark:to-teal-950/20"
              data-testid="ai-tools-strip"
            >
              <div className="w-full sm:flex-1 min-w-[260px]">
                <FuzzySearchInput language={language} onClose={() => { /* inline */ }} />
              </div>
              <div className="flex flex-wrap gap-2 items-start">
                <SmartSynopsisCard
                  book={selectedBook}
                  chapter={selectedChapter}
                  language={language}
                  scope="chapter"
                />
                <SmartSynopsisCard
                  book={selectedBook}
                  language={language}
                  scope="book"
                />
              </div>
            </div>

            {/* Interlinear toggle */}
            <div className="flex justify-center mt-2 mb-2"><NowFreeBadge seenKey="interlinear" language={language} /></div>
            <InterlinearOverlayHint
              interlinearOn={interlinearOn}
              onToggle={handleInterlinearToggle}
              loading={loadingInterlinear}
              taggedCount={Object.keys(taggedByVerse).length}
              selectedTranslation={selectedTranslation}
              language={language}
              strongsSource={strongsTranslation}
            />

            {/* Name meanings toggle */}
            <div className="mt-2 flex flex-wrap justify-center items-center gap-2">
              <label
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/50 dark:border-amber-700/50 bg-amber-50/60 dark:bg-amber-900/20 text-xs font-medium text-amber-800 dark:text-amber-200 cursor-pointer select-none"
                data-testid="names-toggle-label"
              >
                <BookmarkCheck className="w-3.5 h-3.5" />
                <span>{t('reader.nameMeanings')}</span>
                <Switch checked={namesOn} onCheckedChange={handleNamesToggle} data-testid="names-toggle" />
                <NowFreeBadge seenKey="names" language={language} />
              </label>
              <ChapterNamesPanel
                verses={chapterVerses}
                reference={`${selectedBook} ${selectedChapter}`}
                language={language}
                onNavigate={handleRefNavigate}
              />
            </div>
          </div>
        )}
      </div>

      {/* Study Trail breadcrumb — the chain of cross-reference jumps taken */}
      {studyTrail.length >= 2 && (
        <div
          className="mt-4 flex items-center gap-2 rounded-lg border border-blue-200/70 dark:border-blue-800/50 bg-blue-50/60 dark:bg-blue-900/20 px-3 py-2 overflow-x-auto"
          data-testid="study-trail"
        >
          <Route className="w-4 h-4 text-blue-600 dark:text-blue-300 shrink-0" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 shrink-0">
            {t('reader.trail')}
          </span>
          <div className="flex items-center gap-1 min-w-0">
            {studyTrail.map((step, i) => {
              const isCurrent = i === studyTrail.length - 1;
              return (
                <React.Fragment key={`${step.book}-${step.chapter}-${i}`}>
                  {i > 0 && <ChevronRight className="w-3 h-3 text-blue-400 shrink-0" />}
                  <button
                    onClick={() => !isCurrent && jumpToTrail(i)}
                    disabled={isCurrent}
                    className={`shrink-0 text-xs px-2 py-0.5 rounded transition-colors ${
                      isCurrent
                        ? 'bg-blue-600 text-white font-medium cursor-default'
                        : 'text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                    }`}
                    data-testid={`study-trail-step-${i}`}
                  >
                    {getBookName(step.book, language)} {step.chapter}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
          <button
            onClick={saveAndShareTrail}
            disabled={savingPath}
            className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 disabled:opacity-50"
            title={t('reader.saveShareTrail')}
            data-testid="study-trail-share"
          >
            <Share2 className="w-3.5 h-3.5" />
            {t('reader.share')}
          </button>
          <button
            onClick={() => setStudyTrail([])}
            className="shrink-0 text-blue-500 hover:text-blue-700 dark:hover:text-blue-200"
            title={t('reader.clearTrail')}
            data-testid="study-trail-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading chapter...</p>
        </div>
      )}

      {/* Book-unavailable inline banner — shown when a book isn't part of the
          selected translation (e.g. JPS/Leeser are OT-only, Wesley/Weymouth NT-only). */}
      {!isLoading && isUnavailable && (
        <div
          className="my-8 rounded-xl border border-amber-300/60 dark:border-amber-700/50 bg-amber-50/70 dark:bg-amber-900/20 p-5 text-center"
          data-testid="book-unavailable-banner"
        >
          <BookmarkCheck className="w-6 h-6 mx-auto text-amber-500 mb-2" />
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            {t('reader.bookNotIn', { book: getBookName(selectedBook, language), tr: selectedTranslation })}
          </p>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/70 mt-1">
            {t('reader.partialCoverage')}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3 text-xs border-amber-300/60 dark:border-amber-700/60 text-amber-800 dark:text-amber-200"
            onClick={() => { setSelectedTranslation('KJV'); localStorage.setItem('preferred_translation', 'KJV'); }}
            data-testid="book-unavailable-switch-kjv"
          >
            {t('reader.switchToKJV')}
          </Button>
        </div>
      )}

      {/* Fallback notice — the requested version isn't self-hosted offline
          (e.g. copyrighted RVR1960/NVI/NIV), so we serve a seeded public-domain
          text instead. Keeps the reader instant + always populated. */}
      {!isLoading && fallbackTranslation && chapterVerses.length > 0 && (
        <div
          className="mb-4 rounded-lg border border-sky-300/60 dark:border-sky-700/50 bg-sky-50/70 dark:bg-sky-900/20 px-4 py-2.5 text-center"
          data-testid="translation-fallback-banner"
        >
          <p className="text-xs text-sky-800 dark:text-sky-200">
            {t('reader.fallbackNotice', { tr: selectedTranslation, fb: fallbackTranslation })}
          </p>
        </div>
      )}


      {!isLoading && chapterVerses.length > 0 && (
        <BibleIntroButton book={selectedBook} chapter={selectedChapter} translation={selectedTranslation} language={language} />
      )}

      {/* Chapter Verses - With Swipe Navigation */}
      {!isLoading && chapterVerses.length > 0 && (
        <div 
          className="space-y-3 mb-8"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {chapterVerses.map((verse, verseIdx) => {
            const verseReference = `${selectedBook} ${selectedChapter}:${verse.verse}`;
            const highlightColor = highlights[verseReference];
            
            // Inline highlighter look (wraps text line-per-line, not a block)
            const inlineHighlightClasses = {
              yellow: 'bg-yellow-300/80', green: 'bg-green-300/80', blue: 'bg-blue-300/80',
              purple: 'bg-purple-300/80', pink: 'bg-pink-300/80', orange: 'bg-orange-300/80',
              red: 'bg-red-300/80', teal: 'bg-teal-300/80',
            };
            const borderHighlightClasses = {
              yellow: 'border-yellow-400', green: 'border-green-400', blue: 'border-blue-400',
              purple: 'border-purple-400', pink: 'border-pink-400', orange: 'border-orange-400',
              red: 'border-red-400', teal: 'border-teal-400',
            };
            const isSelected = selectedVerseNumbers.includes(verse.verse);

            const fontSizeClasses = {
              sm: 'text-sm',
              base: 'text-base',
              lg: 'text-lg',
              xl: 'text-xl',
              '2xl': 'text-2xl'
            };

            const fontFamilyClasses = {
              serif: 'font-serif',
              sans: 'font-sans',
              mono: 'font-mono'
            };

            return (
              <div 
                key={`${selectedTranslation}-${selectedChapter}-${verse.verse}-${verseIdx}`} 
                className={`flex gap-3 sm:gap-4 group hover:bg-accent/50 p-3 rounded cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-primary ring-inset bg-primary/5 ' : ''
                }${highlightColor ? `border-l-4 ${borderHighlightClasses[highlightColor]}` : ''}`}
                onClick={() => handleVerseClick(verse)}
                {...bindLongPress(`${selectedBook} ${selectedChapter}:${verse.verse}`)}
                style={{ WebkitTouchCallout: 'none' }}
                data-testid={`verse-row-${verse.verse}`}
              >
                <span className="text-lg font-bold text-primary min-w-[40px] flex-shrink-0 select-none flex flex-col items-center gap-0.5">
                  {verse.verse_end && verse.verse_end > verse.verse ? `${verse.verse}-${verse.verse_end}` : verse.verse}
                  {notesMap[verseReference] && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toast(notesMap[verseReference], { description: `${t('reader.yourNote')} · ${verseReference}`, icon: '📝' }); }}
                      title={notesMap[verseReference]}
                      data-testid={`verse-note-indicator-${verse.verse}`}
                      className="text-amber-500 hover:text-amber-600"
                    >
                      <StickyNote className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(trailCounts[verseReference] ||
                    trailCounts[`${selectedBook} ${selectedChapter}`]) > 0 && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <VerseTrailChip
                        verseReference={verseReference}
                        count={
                          trailCounts[verseReference] ||
                          trailCounts[`${selectedBook} ${selectedChapter}`] ||
                          0
                        }
                        language={language}
                      />
                    </span>
                  )}
                </span>
                {(() => {
                  const taggedText = interlinearOn ? taggedByVerse[verse.verse] : null;
                  if (interlinearOn && taggedText) {
                    return (
                      <div className="flex-1">
                        <InterlinearVerse
                          text={taggedText}
                          lexicon={interlinearLexicon}
                          fontSizeClass={fontSizeClasses[fontSize]}
                          fontFamilyClass={fontFamilyClasses[fontFamily]}
                          translation={selectedTranslation}
                        />
                      </div>
                    );
                  }
                  // Strip translator-note <sup>...</sup> spans that some KJV sources leak as literal text.
                  const cleanText = (verse.text || '').replace(/<sup>[\s\S]*?<\/sup>/gi, '');
                  // When the Name-meanings toggle is ON it takes precedence over
                  // Strong's rendering so EVERY verse highlights names (otherwise
                  // Strong's verses would show none — the "only a few names" bug).
                  if (verse.has_strongs && !namesOn) {
                    return (
                      <StrongsVerseText 
                        text={cleanText} 
                        fontSize={fontSize}
                        fontFamily={fontFamily}
                        translation={selectedTranslation}
                      />
                    );
                  }
                  const plainBody = OLD_SPELLING_TRANSLATIONS.includes(selectedTranslation) && !namesOn
                    ? <OldSpanishText text={cleanText} language={language} />
                    : null;
                  return (
                    <p className={`leading-relaxed text-foreground ${fontSizeClasses[fontSize]} ${fontFamilyClasses[fontFamily]}`}>
                      {plainBody && !highlightColor ? plainBody : highlightColor ? (
                        <span
                          className={`${inlineHighlightClasses[highlightColor]} text-slate-900 rounded px-0.5`}
                          style={{ WebkitBoxDecorationBreak: 'clone', boxDecorationBreak: 'clone' }}
                        >
                          {namesOn
                            ? <NameMeaningText text={cleanText} onNavigate={handleRefNavigate} />
                            : plainBody || cleanText}
                        </span>
                      ) : (
                        namesOn
                          ? <NameMeaningText text={cleanText} onNavigate={handleRefNavigate} />
                          : cleanText
                      )}
                    </p>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* No verses */}
      {!isLoading && !isUnavailable && chapterVerses.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">{language === 'es' ? 'No se encontraron versículos para este capítulo.' : 'No verses found for this chapter.'}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-6 border-t">
        <Button
          onClick={goToPreviousChapter}
          disabled={selectedBook === 'Genesis' && selectedChapter === 1}
          size="lg"
          variant="outline"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          {LS('Previous Chapter', 'Capítulo anterior')}
        </Button>

        <Button
          onClick={goToNextChapter}
          size="lg"
          variant="outline"
        >
          {LS('Next Chapter', 'Capítulo siguiente')}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>

      {authDialog}
      {premiumDialog}

      {/* Offline downloads dialog — accessible to everyone (guests too) from
          the reader's ⋮ menu. Lists free self-hosted versions to save on-device. */}
      <Dialog open={isOfflineDialogOpen} onOpenChange={setIsOfflineDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="reader-offline-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('reader.offlineBibles')}
            </DialogTitle>
          </DialogHeader>
          <OfflineBibleSettings language={language} />
        </DialogContent>
      </Dialog>

      {/* YouVersion Action Dialogs */}
      {selectedVerseForAction && (
        <>
          {isMobile ? (
            isActionMenuOpen && (
              <VerseActionMenuMobile
                verse={selectedVerseForAction}
                onClose={handleCloseActionMenu}
                onSave={handleSaveVerse}
                onNote={handleNoteVerse}
                onCopy={handleCopyVerse}
                onShare={handleShareVerse}
                onImage={handleImageVerse}
                onCompare={handleCompareVerse}
                onHighlight={handleHighlightVerse}
                onAudio={handleAudioVerse}
                onPost={handlePostToCommunity}
                selectionCount={selectedVerseNumbers.length}
              />
            )
          ) : (
            <VerseActionMenu
              isOpen={isActionMenuOpen}
              onClose={handleCloseActionMenu}
              verse={selectedVerseForAction}
              onSave={handleSaveVerse}
              onNote={handleNoteVerse}
              onCopy={handleCopyVerse}
              onShare={handleShareVerse}
              onImage={handleImageVerse}
              onCompare={handleCompareVerse}
              onHighlight={handleHighlightVerse}
              onAudio={handleAudioVerse}
              onPost={handlePostToCommunity}
            />
          )}

          {isSocialShareOpen && (
            <SocialShareDialog
              isOpen={isSocialShareOpen}
              onClose={() => {
                setIsSocialShareOpen(false);
              }}
              verse={selectedVerseForAction}
            />
          )}

          {isPostToCommunityOpen && (
            <CreatePostModal
              open={isPostToCommunityOpen}
              onClose={() => {
                setIsPostToCommunityOpen(false);
              }}
              language={language}
              defaultDestination="feed"
              allowDestinationChoice={true}
              initialTab="verse"
              initialVerseText={selectedVerseForAction.text}
              initialReference={selectedVerseForAction.reference}
            />
          )}

          {isQuickCompareOpen && (
            <QuickCompareDialog
              isOpen={isQuickCompareOpen}
              onClose={() => {
                setIsQuickCompareOpen(false);
              }}
              verse={selectedVerseForAction}
            />
          )}

          {isVerseImageDialogOpen && (
            <EnhancedVerseImageCustomizer
              isOpen={isVerseImageDialogOpen}
              onClose={() => {
                setIsVerseImageDialogOpen(false);
              }}
              verse={selectedVerseForAction}
            />
          )}

          {isAudioPlayerOpen && (
            <AudioBiblePlayer
              isOpen={isAudioPlayerOpen}
              onClose={() => {
                setIsAudioPlayerOpen(false);
              }}
              verse={selectedVerseForAction}
              chapterVerses={chapterVerses}
              translation={selectedTranslation}
              initialScope={audioInitialScope}
              onRequestNextChapter={requestAudioNextChapter}
              nextChapterToken={audioNextChapterToken}
            />
          )}

          {isHighlightDialogOpen && (
            <VerseHighlightDialog
              isOpen={isHighlightDialogOpen}
              onClose={() => {
                setIsHighlightDialogOpen(false);
              }}
              verse={selectedVerseForAction}
              onApplyHighlight={handleApplyHighlight}
              onApplyHighlightWithNote={handleApplyHighlightWithNote}
              currentColor={highlights[selectedVerseForAction.reference]}
            />
          )}

          <Dialog open={isNoteDialogOpen} onOpenChange={(v) => { setIsNoteDialogOpen(v); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('reader.addNote')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t('reader.verse')}</Label>
                  <p className="text-sm text-muted-foreground mt-1">{selectedVerseForAction.reference}</p>
                </div>
                <div>
                  <Label>{t('reader.yourNoteLabel')}</Label>
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder={t('reader.notePlaceholder')}
                    rows={4}
                    className="mt-1"
                  />
                </div>
                <Button onClick={handleSaveNote} className="w-full">
                  {t('reader.saveNote')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
      <StrongsModal
        strongsId={wordStudyId}
        isOpen={!!wordStudyId}
        onClose={() => setWordStudyId(null)}
        translation={strongsTranslationFor(selectedTranslation)}
      />
    </div>
  );
};

export default BibleReaderChapterView;
